import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AppService } from 'src/@core/services/app.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { FunnelService } from './funnel.service';
import { LoopService } from '../loop/loop.service';
import {
  autopilotEndDay, AUTONOMY_INFO, AUTONOMY_LEVELS, cloneLoopConfig, DEFAULT_LOOP_CONFIG, FIELD_LABELS, formatTokens,
  hourLabel, LOOP_LIMITS, LoopConfig, readLoopConfig, sameLoopConfig, scheduleText, TOUCH_FIELDS, toggleTouch,
  validateLoopConfig
} from '../loop/loop.logic';
import {
  addStage, autoPriorities, cloneConfig, defaultFunnel, FunnelConfig, goalOutcomes, MAX_STAGES, MAX_STAGE_NAME,
  moveStage, OUT_STAGE, outcomeNames, outcomesByStage, readFunnel, removeStage, roleFor, sameFunnel,
  setOutcomeGoal, setOutcomeStage, STAGE_TEMPS, unmappedOutcomes, validateFunnel
} from './funnel.logic';

type SettingsSection = 'stages' | 'outcomes' | 'target' | 'loop';

/**
 * Project Settings tab: build the funnel (stages, temperature, priority), then
 * place each outcome in a stage and flag the ones that count as a goal. The Loop
 * section edits the autonomous loop's config; it has its own draft and Save so
 * saving one never overwrites the other.
 */
@Component({
  selector: 'hour4u-admin-project-settings',
  templateUrl: './project-settings.component.html',
  styleUrls: ['./project-settings.component.scss']
})
export class ProjectSettingsComponent implements OnChanges {
  @Input() projectDetails: any;
  /** Emits after a successful save so the project view can reload the project. */
  @Output() saved = new EventEmitter<void>();

  profile = this.appService.getCurrentAccount();

  section: SettingsSection = 'stages';
  draft: FunnelConfig = defaultFunnel();
  /** What is stored on the project (null when no funnel has been saved). */
  stored: FunnelConfig | null = null;
  isSaving = false;
  saveError = '';

  readonly temps = STAGE_TEMPS;
  readonly outOfFunnel = OUT_STAGE;
  readonly maxStages = MAX_STAGES;
  readonly maxName = MAX_STAGE_NAME;
  readonly roleFor = roleFor;

  // Loop (its own draft, stored copy and save state)
  loopDraft: LoopConfig = cloneLoopConfig(DEFAULT_LOOP_CONFIG);
  loopStored: LoopConfig = cloneLoopConfig(DEFAULT_LOOP_CONFIG);
  isSavingLoop = false;
  loopSaveError = '';

  readonly autonomyLevels = AUTONOMY_LEVELS;
  readonly autonomyInfo = AUTONOMY_INFO;
  readonly touchFields = TOUCH_FIELDS;
  readonly fieldLabels = FIELD_LABELS;
  readonly limits = LOOP_LIMITS;
  readonly numberKeys = ['batch_size', 'max_test_cases', 'max_changes_per_run', 'max_planned_tasks', 'token_budget'];
  readonly formatTokens = formatTokens;
  readonly hours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
  readonly hourLabel = hourLabel;

  constructor(
    private funnelService: FunnelService,
    private loopService: LoopService,
    private appService: AppService,
    private notify: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['projectDetails'];
    if (!change) { return; }
    const before = change.previousValue;
    const after = change.currentValue;
    // Keep unsaved edits when the same project object is refreshed in the background.
    const sameProject = before && after && before.project_id === after.project_id;
    if (!sameProject || !this.isDirty) {
      this.loadFromProject();
    }
    if (!sameProject || !this.isLoopDirty) {
      this.loadLoopFromProject();
    }
  }

  private loadLoopFromProject() {
    this.loopStored = readLoopConfig(this.projectDetails);
    this.loopDraft = cloneLoopConfig(this.loopStored);
    this.loopSaveError = '';
  }

  private loadFromProject() {
    this.stored = readFunnel(this.projectDetails);
    this.draft = this.stored ? cloneConfig(this.stored) : defaultFunnel();
    this.saveError = '';
  }

  get outcomes(): string[] {
    return outcomeNames(this.projectDetails);
  }

  get errors(): string[] {
    return validateFunnel(this.draft);
  }

  /** True when there is something to save: edits, or a funnel that was never saved. */
  get isDirty(): boolean {
    return !this.stored || !sameFunnel(this.stored, this.draft);
  }

  get canSave(): boolean {
    return this.isDirty && this.errors.length === 0 && !this.isSaving;
  }

  get goals(): string[] {
    return goalOutcomes(this.draft);
  }

  get unmapped(): string[] {
    return unmappedOutcomes(this.draft, this.outcomes);
  }

  get grouped() {
    return outcomesByStage(this.draft);
  }

  /** The cost per goal target as typed. A blank box means no target. */
  get targetValue(): number | null {
    const t = this.draft.target_cost_per_goal;
    return typeof t === 'number' ? t : null;
  }

  setTarget(value: any) {
    const n = value === '' || value === null || value === undefined ? null : Number(value);
    if (n === null || !isFinite(n)) {
      delete this.draft.target_cost_per_goal;
    } else {
      this.draft.target_cost_per_goal = n;
    }
  }

  /** Outcomes placed in a stage, for the stage strip on the outcome screen. */
  outcomesIn(stageId: string): string[] {
    return this.grouped[stageId] || [];
  }

  /** When the loop runs by itself, in a sentence. */
  get scheduleSentence(): string {
    const ist = new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
    return scheduleText(this.loopDraft, this.projectDetails, ist);
  }

  /** The campaign's end date, shown as the default when no autopilot end date is chosen. */
  get campaignEnd(): string | null {
    return autopilotEndDay(DEFAULT_LOOP_CONFIG, this.projectDetails);
  }

  get previewText(): string {
    return this.draft.stages.map(s => s.name || '…').join(' → ');
  }

  setSection(section: SettingsSection) {
    this.section = section;
  }

  // Stages
  add() { this.draft = addStage(this.draft, ''); }
  remove(id: string) { this.draft = removeStage(this.draft, id); }
  move(index: number, delta: number) { this.draft = moveStage(this.draft, index, delta); }
  autoAssign() { this.draft = autoPriorities(this.draft); }
  setTemp(index: number, temp: string) { this.draft.stages[index].temp = temp; }
  stepPriority(index: number, delta: number) {
    const next = this.draft.stages[index].pri + delta;
    this.draft.stages[index].pri = Math.max(0, Math.min(9, next));
  }

  // Outcomes
  stageOf(outcome: string): string {
    const m = this.draft.outcome_map[outcome];
    return m ? m.stage : '';
  }
  isGoal(outcome: string): boolean {
    const m = this.draft.outcome_map[outcome];
    return !!(m && m.goal);
  }
  canBeGoal(outcome: string): boolean {
    const m = this.draft.outcome_map[outcome];
    return !!(m && m.stage !== OUT_STAGE);
  }
  assign(outcome: string, stageId: string) { this.draft = setOutcomeStage(this.draft, outcome, stageId); }
  toggleGoal(outcome: string) { this.draft = setOutcomeGoal(this.draft, outcome, !this.isGoal(outcome)); }

  /** Role a stage plays for the outcomes placed in it. */
  roleOfStage(stageId: string): string {
    const stage = this.draft.stages.filter(s => s.id === stageId)[0];
    return stage ? roleFor(stage.pri) : '';
  }

  discard() {
    this.loadFromProject();
  }

  save() {
    if (!this.canSave || !this.profile || !this.projectDetails) { return; }
    this.isSaving = true;
    this.saveError = '';
    this.funnelService.saveFunnel(this.profile, this.projectDetails.project_id, this.draft).subscribe(
      () => {
        this.isSaving = false;
        this.notify.showMessage('Funnel saved', 2000);
        this.stored = cloneConfig(this.draft);
        this.saved.emit();
      },
      (err) => {
        this.isSaving = false;
        const message = err && err.error && err.error.message;
        this.saveError = message || 'Could not save the funnel. Your changes are still here, try again.';
      }
    );
  }

  // Loop
  get loopErrors(): string[] {
    return validateLoopConfig(this.loopDraft);
  }

  get isLoopDirty(): boolean {
    return !sameLoopConfig(this.loopStored, this.loopDraft);
  }

  get canSaveLoop(): boolean {
    return this.isLoopDirty && this.loopErrors.length === 0 && !this.isSavingLoop;
  }

  /** Message for one number field, or '' when it is fine. */
  limitError(key: string): string {
    const limit = LOOP_LIMITS[key];
    const value = (this.loopDraft as any)[key];
    if (typeof value !== 'number' || !isFinite(value) || Math.floor(value) !== value) {
      return 'Enter a whole number';
    }
    if (value < limit.min || value > limit.max) {
      return 'Between ' + formatTokens(limit.min) + ' and ' + formatTokens(limit.max);
    }
    return '';
  }

  setAutonomy(level: string) {
    this.loopDraft.autonomy = level as any;
  }

  isTouchOn(field: string): boolean {
    return this.loopDraft.allowed_touch.indexOf(field) !== -1;
  }

  setTouch(field: string, on: boolean) {
    this.loopDraft = toggleTouch(this.loopDraft, field, on);
  }

  discardLoop() {
    this.loadLoopFromProject();
  }

  saveLoop() {
    if (!this.canSaveLoop || !this.profile || !this.projectDetails) { return; }
    this.isSavingLoop = true;
    this.loopSaveError = '';
    const saving = cloneLoopConfig(this.loopDraft);
    this.loopService.saveLoopConfig(this.profile, this.projectDetails.project_id, saving).subscribe(
      () => {
        this.isSavingLoop = false;
        this.notify.showMessage('Loop settings saved', 2000);
        this.loopStored = saving;
        this.saved.emit();
      },
      (err) => {
        this.isSavingLoop = false;
        const message = err && err.error && err.error.message;
        this.loopSaveError = message || 'Could not save the loop settings. Your changes are still here, try again.';
      }
    );
  }

  trackByIndex(i: number) { return i; }
}
